import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  createProcedureRevision,
  getRevAllNodes,
} from "../../../redux/procedureRevisionSlice";
import { showSnackbar as showSnackbarAction } from "../../../redux/snackbarSlice";
import { renderRevStatus } from "../../../util/AppUtils.js";
import RenderHtml from "../../AppSpecificConpnents/PartsTable/RenderHtml";
import Snackbar from "../../Snackbar";
import SignatureField from "../../FormFields/SignatureField";
import { ModalActions } from "../../Buttons";
import { FORM_CONSTANTS, VALIDATION_MESSAGES, MODAL_MODES } from "./constants";
import { buildNewRevisionNodeIndexing } from "./newRevisionNodeIndexing";
import "./UnifiedRevisionModal.css";

/**
 * Create New Revision Modal Component
 * Can be used in both Documents (CREATE mode) and AddProcedure (DRAFT mode) contexts
 */
const CreateNewRevisionModal = ({
  // Modal control
  isOpen,
  onClose,
  onSuccess,

  // Mode and context
  mode = MODAL_MODES.DRAFT, // "create" or "draft"

  // Procedure/Part information
  partId,
  partNumber,
  type,
  description,

  // For CREATE mode (Documents context)
  myRevision,
  currentRev,
  revisionData = [],

  // For DRAFT mode (AddProcedure context)
  procedureData,
  revisionOptions = [],

  // User data
  users = [],
  approvedUsers = [],
  releasedUsers = [],
  requiredByDetails = null,

  // Optional components
  RevisionSelectorComponent = null, // Custom revision selector

  // Navigation (for DRAFT mode)
  navigateAfterCreate = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sigPadRef = useRef();

  // Get rule from Redux (for DRAFT mode template selection)
  const { rule } = useSelector((state) => state.procedureTemplateRule);

  // Get users from Redux for DRAFT mode (if not provided via props)
  const reduxUsers = useSelector((state) => state.users);

  // Use Redux users for DRAFT mode, props for CREATE mode
  const effectiveUsers =
    mode === MODAL_MODES.DRAFT && (!users || users.length === 0)
      ? reduxUsers.submittedUsers || []
      : users;
  const effectiveApprovedUsers =
    mode === MODAL_MODES.DRAFT && (!approvedUsers || approvedUsers.length === 0)
      ? reduxUsers.approvedUsers || []
      : approvedUsers;
  const effectiveReleasedUsers =
    mode === MODAL_MODES.DRAFT && (!releasedUsers || releasedUsers.length === 0)
      ? reduxUsers.releasedUsers || []
      : releasedUsers;

  // Form state
  const [revision, setRevision] = useState("A");
  const [submissionBy, setSubmissionBy] = useState("");
  const [approvedBy, setApprovedBy] = useState([]);
  const [releasedBy, setReleasedBy] = useState("");
  const [revisionDetails, setRevisionDetails] = useState("");
  const [password, setPassword] = useState("");
  const [signature, setSignature] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // DRAFT mode specific state
  const [template, setTemplate] = useState("");
  const [selectedTemplateRef, setSelectedTemplateRef] = useState({});

  // Get signature auth methods from localStorage
  const [authMethods, setAuthMethods] = useState([]);
  useEffect(() => {
    try {
      const storedMethods = localStorage.getItem("signature_auth_methods");
      if (storedMethods) {
        const methods = JSON.parse(storedMethods);
        setAuthMethods(Array.isArray(methods) ? methods : []);
      } else {
        // Try to get from loggedinUserInfo as fallback
        const userInfo = localStorage.getItem("loggedinUserInfo");
        if (userInfo) {
          try {
            const parsed = JSON.parse(userInfo);
            if (
              parsed.signature_auth_methods &&
              Array.isArray(parsed.signature_auth_methods)
            ) {
              setAuthMethods(parsed.signature_auth_methods);
            } else {
              // Default to both if not set (backward compatibility)
              setAuthMethods(["password", "signature"]);
            }
          } catch (e) {
            setAuthMethods(["password", "signature"]);
          }
        } else {
          // Default to both if not set (backward compatibility)
          setAuthMethods(["password", "signature"]);
        }
      }
    } catch (error) {
      console.error("Error parsing signature_auth_methods:", error);
      setAuthMethods(["password", "signature"]);
    }
  }, []);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const showSnackbar = (message, type) => {
    if (mode === MODAL_MODES.DRAFT) {
      dispatch(showSnackbarAction({ type, message }));
    } else {
      setSnackbar({ open: true, message, type });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", type: "" });
  };

  // Initialize user selections and reset signature
  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
      // Reset signature when modal opens
      setSignature("");

      if (requiredByDetails && requiredByDetails.length > 0) {
        const submission = requiredByDetails.find(
          (r) => r.purpose === "Submit"
        );
        const approvers = requiredByDetails.filter(
          (r) => r.purpose === "Approve"
        );
        const releaser = requiredByDetails.find((r) => r.purpose === "Release");

        setSubmissionBy(submission?.user_ref || "");
        setApprovedBy(approvers.map((a) => a.user_ref) || []);
        setReleasedBy(releaser?.user_ref || "");
      } else if (effectiveUsers && effectiveUsers.length > 0) {
        if (!submissionBy) setSubmissionBy(effectiveUsers[0].id);
        if (
          effectiveApprovedUsers &&
          effectiveApprovedUsers.length > 0 &&
          approvedBy.length === 0
        ) {
          setApprovedBy([effectiveApprovedUsers[0].id]);
        }
        if (
          effectiveReleasedUsers &&
          effectiveReleasedUsers.length > 0 &&
          !releasedBy
        ) {
          setReleasedBy(effectiveReleasedUsers[0].id);
        }
      }
    }
  }, [
    isOpen,
    effectiveUsers,
    effectiveApprovedUsers,
    effectiveReleasedUsers,
    requiredByDetails,
  ]);

  // Initialize revision details (DRAFT mode)
  useEffect(() => {
    if (
      isOpen &&
      mode === MODAL_MODES.DRAFT &&
      procedureData?.revisionDetails
    ) {
      setRevisionDetails(procedureData.revisionDetails);
    }
  }, [isOpen, mode, procedureData]);

  // Set default revision (DRAFT mode)
  useEffect(() => {
    if (isOpen && mode === MODAL_MODES.DRAFT) {
      if (revisionOptions.length > 0 && revision === "A") {
        setRevision(revisionOptions[0].value);
      } else if (revisionOptions.length === 0) {
        setRevision("A-");
      }
    }
  }, [isOpen, mode, revisionOptions, revision]);

  // Set default template (DRAFT mode)
  useEffect(() => {
    if (isOpen && mode === MODAL_MODES.DRAFT && rule) {
      if (rule.is_blank_template_permitted) {
        setTemplate("Blank");
      } else if (rule.parts && rule.parts.length > 0) {
        const defaultTemplate = rule.parts.find((p) => p.is_default_template);
        const selectedTemplate = defaultTemplate || rule.parts[0];
        if (selectedTemplate) {
          setSelectedTemplateRef(selectedTemplate);
          setTemplate(selectedTemplate.part_id);
        }
      }
    }
  }, [isOpen, mode, rule]);

  const handleEndDrawing = () => {
    if (sigPadRef.current?.getCanvas()) {
      // Check if canvas is empty
      if (sigPadRef.current.isEmpty()) {
        setSignature("");
      } else {
        const dataUrl = sigPadRef.current.getCanvas().toDataURL("image/png");
        setSignature(dataUrl);
      }
    }
  };

  const handleApproverChange = (userId, checked) => {
    if (!checked && approvedBy.length === 1) {
      showSnackbar(VALIDATION_MESSAGES.AT_LEAST_ONE_APPROVER, "error");
      return;
    }
    setApprovedBy((prev) =>
      checked ? [...prev, userId] : prev.filter((id) => id !== userId)
    );
  };

  const validate = () => {
    if (!revision || revision === "A") {
      showSnackbar(VALIDATION_MESSAGES.NO_REVISION, "error");
      return false;
    }
    if (!revisionDetails.trim()) {
      showSnackbar(VALIDATION_MESSAGES.REVISION_DETAILS_REQUIRED, "error");
      return false;
    }
    // Only validate password if it's in auth methods
    if (authMethods.includes("password") && !password.trim()) {
      showSnackbar(VALIDATION_MESSAGES.PASSWORD_REQUIRED, "error");
      return false;
    }
    // Only validate signature if it's in auth methods
    if (authMethods.includes("signature")) {
      const isSignatureEmpty = sigPadRef.current?.isEmpty() ?? true;
      if (!signature || isSignatureEmpty) {
        showSnackbar(VALIDATION_MESSAGES.SIGNATURE_REQUIRED, "error");
        return false;
      }
    }
    if (!submissionBy || !releasedBy || approvedBy.length === 0) {
      showSnackbar(VALIDATION_MESSAGES.USER_SELECTIONS_REQUIRED, "error");
      return false;
    }
    return true;
  };

  const createRevision = async () => {
    if (!validate()) return;

    setIsLoading(true);
    
    // Flag to track if we encountered an error
    let hasError = false;
    let errorOccurred = null;

    const required_by_details = [
      {
        purpose: "Submit",
        user_ref: submissionBy,
        is_open: true,
        user: effectiveUsers.find((u) => u.id === submissionBy) || {},
      },
      ...approvedBy.map((id) => ({
        purpose: "Approve",
        user_ref: id,
        is_open: true,
        user: effectiveUsers.find((u) => u.id === id) || {},
      })),
      {
        purpose: "Release",
        user_ref: releasedBy,
        is_open: true,
        user: effectiveUsers.find((u) => u.id === releasedBy) || {},
      },
    ];

    try {
      let payload;

      if (mode === MODAL_MODES.CREATE) {
        // CREATE mode logic (Documents context)
        const currentRevision = revisionData.find(
          (rev) =>
            rev.procedure_revision_id === currentRev.procedure_revision_id
        );

        if (!currentRevision) {
          showSnackbar(VALIDATION_MESSAGES.CURRENT_REVISION_NOT_FOUND, "error");
          setIsLoading(false);
          return;
        }

        // Fetch nodes data for the current revision
        let nodesData = null;
        try {
          const nodesResult = await dispatch(
            getRevAllNodes({
              partId: partId,
              revisionId: currentRev.procedure_revision_id,
            })
          );
          nodesData = nodesResult.payload;
        } catch (error) {
          console.error("Error fetching nodes data:", error);
        }

        // Generate node indexing for new revision
        const nodeIndexing = buildNewRevisionNodeIndexing({
          templateNodes: nodesData || [],
          sourceDocument: {
            partNumber: partNumber,
            revision: myRevision,
          },
          targetDocument: {
            partNumber: partNumber,
            revision: revision,
          },
        });

        const prevRevisionStatus = renderRevStatus(currentRevision);

        const newRevisionData = {
          rev: revision,
          revision_details: revisionDetails,
          part_ref: partId,
          is_latest: true,
          is_maintained_outside_system: false,
          status_changed_to: "Not Submitted",
          is_in_release_history: false,
          past_procedure_revisions_reviewed: {},
          prev_revision_status: prevRevisionStatus || null,
          required_by_details,
          based_on_version_ref: currentRev.version_id_ref,
          template_ref: currentRevision.part_ref,
          revision_ref: currentRevision.procedure_revision_id,
          node_indexing: nodeIndexing,
          audit_trail_data: {
            signature: authMethods.includes("signature") ? signature : "",
            ...(authMethods.includes("password") && password
              ? { hash: password }
              : {}),
            ...(!authMethods.includes("password")
              ? {
                  is_password_required: false,
                }
              : {
                  is_password_required: true,
                }),
            comment: revisionDetails,
            password,
          },
        };

        const nodeindexingitems = Object.values(newRevisionData.node_indexing);
        try {
          payload = await dispatch(
            createProcedureRevision({
              revisionData: newRevisionData,
              version_content: {
                procedure_number: partNumber,
                type,
                description,
                from_part_ref: partNumber,
                from_part_rev: myRevision,
                to_part_ref: partNumber,
                to_part_rev: revision,
                revisionNodes:
                nodeindexingitems[nodeindexingitems.length - 1]?.newContent,
                partNumber: partNumber,
                status: "Not Submitted",
                item_code_type:type,
              },
            })
          ).unwrap();
        } catch (unwrapError) {
          // If unwrap throws, mark error and re-throw it to be caught by outer catch
          hasError = true;
          errorOccurred = unwrapError;
          console.error("Error from unwrap (CREATE mode):", unwrapError);
          throw unwrapError;
        }
        
        // Validate response immediately after unwrap
        // Check for explicit error indicators
        if (payload?.success === false) {
          const errorMsg = payload?.message || payload?.error || "Operation failed";
          console.error("Server returned success: false:", errorMsg);
          hasError = true;
          errorOccurred = new Error(errorMsg);
          throw errorOccurred;
        }
        
        // Check if response is missing required structure
        if (!payload || !payload.results) {
          const errorMsg = payload?.message || payload?.error || "Invalid response from server";
          console.error("Invalid payload structure (CREATE mode):", payload);
          hasError = true;
          errorOccurred = new Error(errorMsg);
          throw errorOccurred;
        }
        
        // Additional check: look for error messages in the response even if success is true
        const responseStr = JSON.stringify(payload).toLowerCase();
        if (responseStr.includes("incorrect password") || 
            responseStr.includes("wrong password") || 
            responseStr.includes("invalid password")) {
          const errorMsg = payload?.message || payload?.error || "Incorrect password";
          console.error("Password error detected in response (CREATE mode):", errorMsg);
          hasError = true;
          errorOccurred = new Error(errorMsg);
          throw errorOccurred;
        }
      } else {
        // DRAFT mode logic (AddProcedure context)
        let latestReleasedRevision = null;
        if (procedureData?.revisionData?.length > 0) {
          const sortedRevisions = [...procedureData.revisionData].sort(
            (a, b) => {
              const dateA = new Date(a.date_released || a.created_at);
              const dateB = new Date(b.date_released || b.created_at);
              return dateB - dateA;
            }
          );

          latestReleasedRevision =
            sortedRevisions.find((rev) =>
              rev.status_changes?.some(
                (sc) =>
                  sc.is_latest &&
                  sc.status_changed_to?.toLowerCase() === "released"
              )
            ) || sortedRevisions[0];
        }

        const revision_ref =
          latestReleasedRevision?.procedure_revision_id || null;
        const latest_revision_part_ref =
          latestReleasedRevision?.part_ref || null;

        const revisionPayload = {
          rev: revision,
          revision_details: revisionDetails,
          part_ref: procedureData.partId,
          template_ref: template || latest_revision_part_ref || "",
          is_latest: true,
          is_maintained_outside_system: false,
          status_changed_to: "Not Submitted",
          is_in_release_history: false,
          past_procedure_revisions_reviewed: {},
          required_by_details,
          reference: procedureData.references,
          revision_ref:
            procedureData.templateProcedureRevisionId || revision_ref || "",
          audit_trail_data: {
            signature: authMethods.includes("signature") ? signature : "",
            ...(authMethods.includes("password") && password
              ? { hash: password }
              : {}),
            ...(!authMethods.includes("password") ? {
              is_password_required: false,
            }:{
              is_password_required: true,
            }),
            comment: revisionDetails,
            password,
          },
        };

        let selectedTemplate = "";
        let revInfo = {};

        if (revisionPayload.template_ref === "Blank") {
          revisionPayload.template_ref = "";
          revisionPayload.revision_ref = "";
          selectedTemplate = "Blank";
          revisionPayload.based_on_version_ref = "";
          revisionPayload.node_indexing = {};
        } else {
          if (!selectedTemplateRef?.latest_procedure_revision) {
            showSnackbar(VALIDATION_MESSAGES.TEMPLATE_REQUIRED, "error");
            setIsLoading(false);
            return;
          }

          // Fetch nodes data for the current revision

          let nodesData = null;
          try {
            const nodesResult = await dispatch(
              getRevAllNodes({
                partId: selectedTemplateRef.part_id,
                revisionId:
                  selectedTemplateRef.latest_procedure_revision
                    .procedure_revision_id,
              })
            );
            nodesData = nodesResult.payload;
          } catch (error) {
            console.error("Error fetching nodes data:", error);
          }

          // Generate node indexing for new revision
          const nodeIndexing = buildNewRevisionNodeIndexing({
            templateNodes: nodesData || [],
            sourceDocument: {
              partNumber: selectedTemplateRef.part_number,
              revision: selectedTemplateRef.latest_procedure_revision.rev,
            },
            targetDocument: {
              partNumber: procedureData.procedureNumber,
              revision: revision,
            },
          });
          revisionPayload.node_indexing = nodeIndexing;
          revisionPayload.revision_ref =
            selectedTemplateRef.latest_procedure_revision.procedure_revision_id;
          selectedTemplate = `${selectedTemplateRef.part_number}, Rev ${
            selectedTemplateRef.latest_procedure_revision?.rev
          }, ${selectedTemplateRef.description} ${
            selectedTemplateRef.is_default_template ? "(Default)" : ""
          }`;
          revisionPayload.based_on_version_ref =
            selectedTemplateRef.latest_procedure_revision.version_id_ref;
          revInfo = {
            from_part_ref: selectedTemplateRef.part_number,
            from_part_rev: selectedTemplateRef.latest_procedure_revision.rev,
            to_part_ref: procedureData.procedureNumber,
            to_part_rev: revision,
          };
        }

        const nodeindexingitems = Object.values(revisionPayload.node_indexing);

        revisionPayload.prev_revision_status = revisionPayload.template_ref === "" ? null : "Released";
        try {
          payload = await dispatch(
            createProcedureRevision({
              revisionData: revisionPayload,
              version_content: {
                selectedTemplate,
                procedure_number: procedureData.procedureNumber,
                type: procedureData.procedureType,
                description: procedureData.description,
                ...revInfo,
                revisionNodes:
                  nodeindexingitems[nodeindexingitems.length - 1]?.newContent,
                status: "Not Submitted",
                item_code_type:procedureData.procedureType,
                partNumber: procedureData.procedureNumber,
              },
            })
          ).unwrap();
          
        } catch (unwrapError) {
          // If unwrap throws, mark error and re-throw it to be caught by outer catch
          hasError = true;
          errorOccurred = unwrapError;
          console.error("========== UNWRAP ERROR (DRAFT mode) ==========");
          console.error("Error from unwrap:", unwrapError);
          console.error("Error type:", typeof unwrapError);
          console.error("Error message:", unwrapError?.message || unwrapError);
          console.error("hasError set to:", hasError);
          console.error("================================================");
          throw unwrapError;
        }
        
        // Validate response immediately after unwrap
        // Check for explicit error indicators
        if (payload?.success === false) {
          const errorMsg = payload?.message || payload?.error || "Operation failed";
          console.error("Server returned success: false:", errorMsg);
          hasError = true;
          errorOccurred = new Error(errorMsg);
          throw errorOccurred;
        }
        
        // Check if response is missing required structure
        if (!payload || !payload.results) {
          const errorMsg = payload?.message || payload?.error || "Invalid response from server";
          console.error("Invalid payload structure (DRAFT mode):", payload);
          hasError = true;
          errorOccurred = new Error(errorMsg);
          throw errorOccurred;
        }
        
        // Additional check: look for error messages in the response even if success is true
        const responseStr = JSON.stringify(payload).toLowerCase();
        if (responseStr.includes("incorrect password") || 
            responseStr.includes("wrong password") || 
            responseStr.includes("invalid password") ||
            responseStr.includes("password incorrect") ||
            responseStr.includes("password is incorrect")) {
          const errorMsg = payload?.message || payload?.error || "Incorrect password";
          console.error("========== PASSWORD ERROR DETECTED (DRAFT mode) ==========");
          console.error("Password error detected in response:", errorMsg);
          console.error("Response string snippet:", responseStr.substring(0, 500));
          hasError = true;
          errorOccurred = new Error(errorMsg);
          throw errorOccurred;
        }
        
      }

      // Final validation: ensure payload exists and has expected structure
      if (!payload || !payload.results) {
        console.error("FATAL: Invalid payload structure");
        hasError = true;
        errorOccurred = new Error("Invalid response from server");
        throw errorOccurred;
      }

      const createdRevisionId =
        payload?.results?.tableOfProcedures?.procedure_revision_ref;

      if (!createdRevisionId && mode === MODAL_MODES.DRAFT) {
        console.error("FATAL: Missing revision ID in DRAFT mode");
        hasError = true;
        errorOccurred = new Error("Missing revision ID in response.");
        throw errorOccurred;
      }

      // Final check: ensure we have a valid revision ID before proceeding
      if (!createdRevisionId) {
        console.error("FATAL: No revision ID found in response, treating as error");
        hasError = true;
        errorOccurred = new Error("Failed to create revision: No revision ID returned");
        throw errorOccurred;
      }
      
      // CRITICAL: One more password error check before proceeding
      const finalPayloadStr = JSON.stringify(payload).toLowerCase();
      if (finalPayloadStr.includes("incorrect password") || 
          finalPayloadStr.includes("wrong password") || 
          finalPayloadStr.includes("invalid password") ||
          finalPayloadStr.includes("password incorrect") ||
          finalPayloadStr.includes("password is incorrect") ||
          finalPayloadStr.includes("password error")) {
        console.error("========== FINAL PASSWORD ERROR CHECK DETECTED ==========");
        console.error("Password error found in final payload check");
        hasError = true;
        errorOccurred = new Error("Incorrect password");
        throw errorOccurred;
      }

      // Double-check: if we have an error flag set, don't proceed with success flow
      if (hasError || errorOccurred) {
        console.error("Error flag detected, aborting success flow. hasError:", hasError, "errorOccurred:", errorOccurred);
        throw errorOccurred || new Error("Error occurred during revision creation");
      }

      // Triple-check: Validate the payload one more time before success
      if (!payload || !payload.results || !payload.results.tableOfProcedures) {
        console.error("Final validation failed - invalid payload structure");
        hasError = true;
        errorOccurred = new Error("Invalid response structure from server");
        throw errorOccurred;
      }

      // Final password error check - scan the entire response one more time
      const finalCheckStr = JSON.stringify(payload).toLowerCase();
      if (finalCheckStr.includes("incorrect password") || 
          finalCheckStr.includes("wrong password") || 
          finalCheckStr.includes("invalid password")) {
        console.error("Final password error check detected error in response");
        hasError = true;
        errorOccurred = new Error("Incorrect password");
        throw errorOccurred;
      }

      // Only set loading to false and show success if we got here without errors
      // AND we've passed all validation checks
      if (hasError || errorOccurred) {
        console.error("ERROR: Attempted to show success with error flags set!");
        throw errorOccurred || new Error("Cannot proceed - error detected");
      }

      // ABSOLUTE FINAL CHECK: Verify error flags one more time before any success operations
      if (hasError || errorOccurred) {
        console.error("FATAL ERROR: Error flags detected right before success flow!");
        console.error("hasError:", hasError, "errorOccurred:", errorOccurred);
        throw errorOccurred || new Error("Cannot proceed - error detected");
      }
      
      // CRITICAL: If password was required and provided, we need to ensure it was validated
      // If the server created the revision even with wrong password, we should detect it
      // Check if password was in auth methods and was provided
      const passwordWasRequired = authMethods.includes("password");
      const passwordWasProvided = password && password.trim().length > 0;
      
      // If password was required and provided, but we got a successful response,
      // we need to be extra cautious - the server might have created it with wrong password
      // In this case, we should check the response more carefully
      if (passwordWasRequired && passwordWasProvided) {
        // Check if there's any indication in the response that password validation failed
        const payloadStr = JSON.stringify(payload || {}).toLowerCase();
        const hasPasswordErrorIndicators = 
          payloadStr.includes("incorrect password") ||
          payloadStr.includes("wrong password") ||
          payloadStr.includes("invalid password") ||
          payloadStr.includes("password incorrect") ||
          payloadStr.includes("password validation failed") ||
          payloadStr.includes("authentication failed");
        
        if (hasPasswordErrorIndicators) {
          console.error("========== PASSWORD ERROR INDICATORS FOUND IN RESPONSE ==========");
          console.error("Even though response looks successful, password error indicators found!");
          hasError = true;
          errorOccurred = new Error("Incorrect password");
          throw errorOccurred;
        }
        
        // Additional check: If the server response doesn't explicitly confirm password validation,
        // and we can't verify it was correct, we should be cautious
        // However, if we got here, the server returned success, so we'll trust it
        // But we'll log it for debugging
      }
      
      // ONE MORE FINAL CHECK before any success operations
      if (hasError || errorOccurred) {
        console.error("FATAL: Error flags detected after all checks!");
        throw errorOccurred || new Error("Cannot proceed - error detected");
      }
      
      setIsLoading(false);
      showSnackbar("Revision created successfully!", "success");
      
      // Close modal first
      onClose();

      // Only call onSuccess if we have a valid revision ID and no errors
      // Add one more check here as final safeguard
      if (onSuccess && createdRevisionId && !hasError && !errorOccurred) {
        try {
          onSuccess(createdRevisionId);
        } catch (callbackError) {
          console.error("Error in onSuccess callback:", callbackError);
        }
      } else {
      }

      // Navigate for DRAFT mode only if we have a valid revision ID and no errors
      if (mode === MODAL_MODES.DRAFT && navigateAfterCreate && createdRevisionId && !hasError && !errorOccurred) {
        try {
          navigate(
            `/revision-submission/${procedureData.partId}/${revision}/${
              procedureData.procedureType
            }/${procedureData.procedureNumber}/${createdRevisionId}/${
              procedureData.description || "-"
            }`,
            { state: { status: "Not Submitted" } }
          );
        } catch (navError) {
          console.error("Error during navigation:", navError);
        }
      } else {
      }
    } catch (error) {
      // Mark that we encountered an error - CRITICAL: This must be set first
      hasError = true;
      errorOccurred = error;
      
      console.error("========== ERROR CAUGHT IN CREATE REVISION ==========");
      console.error("Error creating revision - Full error object:", error);
      console.error("Error type:", typeof error);
      console.error("Error stringified:", JSON.stringify(error, null, 2));
      console.error("hasError flag:", hasError);
      console.error("errorOccurred:", errorOccurred);
      
      // Check if error is a string (from rejectWithValue)
      if (typeof error === "string") {
        console.error("Error is a STRING:", error);
      } else if (error?.message) {
        console.error("Error has message property:", error.message);
      }
      console.error("=====================================================");
      
      // Extract error message from various possible locations
      // When using .unwrap(), the error is the value passed to rejectWithValue()
      // In procedureRevisionSlice, it passes: error.response?.data?.message || error.response?.data?.error || error.message || "Error creating revision"
      let errorMessage = "Unknown error occurred";

      if (error) {
        // Check if error is a string (from rejectWithValue passing message directly)
        if (typeof error === "string") {
          errorMessage = error;
        } else if (error && typeof error === "object") {
          // Check for {success: false, message: "Incorrect password"} structure
          if (error.success === false && error.message) {
            errorMessage = error.message;
          } else if (error.message) {
            errorMessage = error.message;
          } else if (error.error) {
            errorMessage = error.error;
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.payload?.message) {
            errorMessage = error.payload.message;
          } else if (error.payload?.error) {
            errorMessage = error.payload.error;
          } else {
            // If error is an object, try to stringify it or get first property
            errorMessage = JSON.stringify(error);
          }
        }
      }

      // Normalize error message for password errors
      const normalizedError = errorMessage?.toLowerCase() || "";
      const isPasswordError = 
        normalizedError.includes("incorrect password") ||
        normalizedError.includes("wrong password") ||
        normalizedError.includes("invalid password") ||
        normalizedError === "incorrect password";

      // For password errors, show the message directly
      if (isPasswordError) {
        console.error("========== PASSWORD ERROR - SHOWING ERROR MESSAGE ==========");
        console.error("Password error detected, showing error message");
        showSnackbar("Incorrect password", "error");
      } else {
        console.error("========== NON-PASSWORD ERROR - SHOWING ERROR MESSAGE ==========");
        console.error("Non-password error, showing generic error");
        showSnackbar("Error creating revision: " + errorMessage, "error");
      }
      setIsLoading(false);
      
      // CRITICAL: Return early to prevent any further execution
      // This ensures onSuccess, onClose, and navigation never happen on error
      console.error("========== RETURNING FROM ERROR HANDLER ==========");
      console.error("NO SUCCESS CALLBACKS WILL EXECUTE");
      console.error("Final state - hasError:", hasError, "errorOccurred:", errorOccurred);
      console.error("onSuccess will NOT be called");
      console.error("Modal will NOT close");
      console.error("Navigation will NOT happen");
      console.error("==================================================");
      
      // ABSOLUTE GUARANTEE: Return immediately, no further code execution
      return;
    }
    
    // EXTRA SAFEGUARD: This should never execute if error occurred, but just in case
    if (hasError || errorOccurred) {
      console.error("========== FATAL: REACHED END WITH ERROR FLAGS ==========");
      console.error("This should NEVER happen - error flags are set but we reached end of function!");
      console.error("hasError:", hasError, "errorOccurred:", errorOccurred);
      setIsLoading(false);
      showSnackbar("An error occurred. Please try again.", "error");
      return;
    }
    
  };

  if (!isOpen) return null;

  const procedureInfo =
    mode === MODAL_MODES.CREATE
      ? { type, number: partNumber, description: currentRev?.part_description }
      : {
          type: procedureData?.procedureType,
          number: procedureData?.procedureNumber,
          description: procedureData?.description,
        };

  return (
    <>
      <div className="revision-modal-overlay">
        <div className="revision-modal-content">
          {/* Header */}
          <div className="revision-modal-header">
            <h3 className="revision-modal-title">Draft New Revision</h3>
            <button
              onClick={onClose}
              className="revision-close-button"
              title="Close"
              aria-label="Close modal"
            >
              <FontAwesomeIcon icon={faTimes} size="1x" color="gray" />
            </button>
          </div>

          <hr className="divider" />

          {/* Procedure Info */}
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            marginBottom: "15px",
            flexWrap: "wrap"
          }}>
            <strong
              style={{
                fontSize: "14px",
                marginRight: "10px",
              }}
            >
              {procedureInfo.type} {procedureInfo.number}
            </strong>
            <span style={{ fontSize: "12px" }}>
              <RenderHtml content={procedureInfo.description} />
            </span>
          </div>

          {/* Custom Revision Selector or Default */}
          {RevisionSelectorComponent ? (
            <RevisionSelectorComponent
              revision={revision}
              setRevision={setRevision}
              revisionData={revisionData}
              myRevision={myRevision}
              currentRev={currentRev}
              revisionOptions={revisionOptions}
              procedureData={procedureData}
            />
          ) : (
            mode === MODAL_MODES.DRAFT && (
              <div
                className="form-group"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: "8px",
                  width: "100%",
                }}
              >
                <label
                  style={{
                    marginRight: "10px",
                    whiteSpace: "nowrap",
                    minWidth: "50px",
                    fontSize: "12px",
                  }}
                >
                  <b>Revision:</b>
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "20px",
                    width: "100%",
                  }}
                >
                  {revisionOptions.length === 0 ? (
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <input
                        type="radio"
                        name="revision"
                        value="A-"
                        checked={revision === "A-"}
                        onChange={() => setRevision("A-")}
                      />
                      <span style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                        A- (Major)
                      </span>
                    </label>
                  ) : (
                    revisionOptions.map((opt) => (
                      <label
                        key={opt.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "12px",
                          marginBottom: "6px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <input
                          type="radio"
                          name="revision"
                          value={opt.value}
                          checked={revision === opt.value}
                          onChange={() => setRevision(opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))
                  )}
                </div>
              </div>
            )
          )}

          {/* Template Selection (DRAFT mode only) */}
          {mode === MODAL_MODES.DRAFT && rule && (
            <div
              className="form-group"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                marginBottom: "8px",
                width: "100%",
                whiteSpace: "nowrap",
              }}
            >
              <label
                style={{
                  marginRight: "10px",
                  whiteSpace: "nowrap",
                  minWidth: "80px",
                  fontSize: "12px",
                }}
              >
                <b>Select Template:</b>
              </label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "start",
                  alignItems: "start",
                  gap: "8px",
                }}
              >
                {rule.is_blank_template_permitted !== null && (
                  <label
                    style={{
                      display: "flex",
                      alignItems: "start",
                      gap: "8px",
                      fontWeight: "normal",
                      whiteSpace: "nowrap",
                      textAlign: "left",
                    }}
                  >
                    <input
                      type="radio"
                      name="template"
                      checked={template === "Blank"}
                      style={{
                        transform: "scale(0.8)",
                        width: "15px",
                        marginTop: "-1px",
                      }}
                      onChange={() => {
                        setTemplate("Blank");
                        setSelectedTemplateRef(null);
                      }}
                    />
                    <span style={{ fontSize: "12px" }}>
                      Blank{" "}
                      {rule.is_blank_template_permitted && (
                        <span style={{ color: "rgb(0, 123, 255)" }}>
                          (Default)
                        </span>
                      )}
                    </span>
                  </label>
                )}
                {rule.parts?.map((row) => (
                  <label
                    key={row.part_number}
                    style={{
                      display: "flex",
                      alignItems: "start",
                      gap: "8px",
                      fontWeight: "normal",
                      whiteSpace: "nowrap",
                      textAlign: "left",
                    }}
                  >
                    <input
                      type="radio"
                      name="template"
                      value={row.part_id}
                      checked={template === row.part_id}
                      style={{
                        transform: "scale(0.8)",
                        width: "15px",
                        marginTop: "-1px",
                      }}
                      onChange={() => {
                        setTemplate(row.part_id);
                        setSelectedTemplateRef(row);
                      }}
                    />
                    <span style={{ fontSize: "12px" }}>
                      {row.part_number}, Rev{" "}
                      {row.latest_procedure_revision?.rev},{" "}
                      <RenderHtml content={row.description} />{" "}
                      {row.is_default_template && (
                        <span style={{ color: "rgb(0, 123, 255)" }}>
                          (Default)
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Requested By */}
          <div
            className="form-group"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: "8px",
              width: "100%",
            }}
          >
            <label
              style={{
                marginRight: "10px",
                whiteSpace: "nowrap",
                minWidth: "120px",
                fontSize: "12px",
              }}
            >
              <b>Requested By:</b>
            </label>
            <span
              style={{
                fontSize: "12px",
                color: "#666",
                marginLeft: "-40px",
                marginTop: "-5px",
              }}
            >
              {localStorage.getItem("user_name")}
            </span>
          </div>

          {/* Submission Required By */}
          <div
            className="form-group"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: "8px",
              width: "100%",
            }}
          >
            <label
              style={{
                marginRight: "10px",
                whiteSpace: "nowrap",
                minWidth: "120px",
                fontSize: "12px",
              }}
            >
              <b>Submission Required By:</b>
            </label>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "20px",
                alignItems: "center",
                whiteSpace: "nowrap",
                overflowX: "auto",
                maxWidth: "340px",
                padding: "0",
              }}
              className="custom-scrollbar"
            >
              {effectiveUsers && effectiveUsers.length > 0 ? (
                effectiveUsers.map((user) => (
                  <label
                    key={user.id}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "5px",
                      fontWeight: "normal",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    <input
                      type="radio"
                      name="submissionBy"
                      value={user.id}
                      checked={submissionBy === user.id}
                      onChange={() => setSubmissionBy(user.id)}
                      style={{ transform: "scale(0.8)" }}
                    />
                    {user.first_name} {user.last_name}
                  </label>
                ))
              ) : (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  No users available
                </span>
              )}
            </div>
          </div>

          {/* Approval Required from */}
          <div
            className="form-group"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: "8px",
              width: "100%",
            }}
          >
            <label
              style={{
                marginRight: "10px",
                whiteSpace: "nowrap",
                minWidth: "120px",
                fontSize: "12px",
              }}
            >
              <b>Approval Required from:</b>
            </label>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "20px",
                alignItems: "center",
                overflowX: "auto",
                maxWidth: "340px",
                padding: "0",
              }}
              className="custom-scrollbar"
            >
              {effectiveApprovedUsers && effectiveApprovedUsers.length > 0 ? (
                effectiveApprovedUsers.map((user) => (
                  <label
                    key={user.id}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "5px",
                      fontWeight: "normal",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      value={user.id}
                      checked={approvedBy.includes(user.id)}
                      onChange={(e) =>
                        handleApproverChange(user.id, e.target.checked)
                      }
                      style={{ transform: "scale(0.6)" }}
                    />
                    {user.first_name} {user.last_name}
                  </label>
                ))
              ) : (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  No approvers available
                </span>
              )}
            </div>
          </div>

          {/* Release Required from */}
          <div
            className="form-group"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: "8px",
              width: "100%",
              whiteSpace: "nowrap",
            }}
          >
            <label
              style={{
                marginRight: "10px",
                whiteSpace: "nowrap",
                minWidth: "120px",
                fontSize: "12px",
              }}
            >
              <b>Release Required from:</b>
            </label>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "20px",
                alignItems: "center",
                overflowX: "auto",
                maxWidth: "340px",
                padding: "0",
              }}
              className="custom-scrollbar"
            >
              {effectiveReleasedUsers && effectiveReleasedUsers.length > 0 ? (
                effectiveReleasedUsers.map((user) => (
                  <label
                    key={user.id}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "5px",
                      fontWeight: "normal",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    <input
                      type="radio"
                      name="releasedBy"
                      value={user.id}
                      checked={releasedBy === user.id}
                      onChange={() => setReleasedBy(user.id)}
                      style={{ transform: "scale(0.8)" }}
                    />
                    {user.first_name} {user.last_name}
                  </label>
                ))
              ) : (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  No releasers available
                </span>
              )}
            </div>
          </div>

          {/* Revision Details */}
          <div className="form-group" style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "12px" }}>
              {mode === MODAL_MODES.DRAFT ? (
                <b>Enter The Revision Details</b>
              ) : (
                "Enter The Revision Details"
              )}
              <span className="required" style={{ marginLeft: "-1px" }}>
                *
              </span>
            </label>
            <textarea
              className="input-textarea"
              placeholder="Add Video and Risk Analysis Attachment"
              value={revisionDetails}
              onChange={(e) => setRevisionDetails(e.target.value)}
              style={{
                fontSize: "12px",
                padding: "5px",
                width: FORM_CONSTANTS.FIELD_WIDTH,
                minHeight: "60px",
              }}
            />
          </div>

          {/* Password - Only show if password is in auth methods */}
          {authMethods.includes("password") && (
            <div className="form-group" style={{ marginBottom: "8px" }}>
              <label style={{ fontSize: "12px" }}>
                {mode === MODAL_MODES.DRAFT ? (
                  <b>Enter Password</b>
                ) : (
                  "Enter Password"
                )}
                <span className="required" style={{ marginLeft: "-1px" }}>
                  *
                </span>
              </label>
              <input
                type="password"
                className="input-field"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  fontSize: "13px",
                  padding: "5px",
                  width: FORM_CONSTANTS.FIELD_WIDTH,
                }}
              />
            </div>
          )}

          {/* Signature - Only show if signature is in auth methods */}
          {authMethods.includes("signature") && (
            <SignatureField
              ref={sigPadRef}
              label="Sign Here"
              required={true}
              width={FORM_CONSTANTS.FIELD_WIDTH}
              height={FORM_CONSTANTS.SIGNATURE_HEIGHT}
              onEnd={handleEndDrawing}
            />
          )}

          {/* Action Buttons */}
          <ModalActions
            onClose={onClose}
            onSubmit={createRevision}
            submitLabel="Create Revision"
            loading={isLoading}
            loadingText="Creating..."
          />
        </div>
      </div>

      {/* Snackbar (CREATE mode only) */}
      {mode === MODAL_MODES.CREATE && snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </>
  );
};

export default CreateNewRevisionModal;
